import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Star,
  Eye,
  MagnifyingGlass,
  ChatCenteredText,
  ShieldCheck,
  ThumbsUp,
  ThumbsDown,
  X,
  Funnel,
  CircleNotch,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { apiClient, useAuthStore } from '@dukene/shared';

interface Review {
  id: string;
  productId: string;
  customerId: string;
  rating: number;
  title?: string;
  comment?: string;
  verified: boolean;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  customer: {
    firstName: string;
    lastName: string;
  };
  product: {
    title: string;
    images?: { url: string }[];
  };
}

interface ReviewsSettings {
  autoApprove: boolean;
}

export default function ReviewsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>(
    'ALL'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  // Fetch reviews
  const { data: reviewsData, isLoading } = useQuery({
    queryKey: ['merchant-reviews', activeStoreId, statusFilter],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (statusFilter !== 'ALL') {
          params.append('status', statusFilter);
        }
        const data = await apiClient.get<Review[]>(`/products/reviews?${params}`);
        return data || [];
      } catch (error) {
        console.error('Error fetching reviews:', error);
        return [] as Review[];
      }
    },
  });

  // Fetch settings
  const { data: settingsData } = useQuery({
    queryKey: ['review-settings', activeStoreId],
    queryFn: async () => {
      try {
        const data = await apiClient.get<ReviewsSettings>('/products/reviews/settings');
        return data || { autoApprove: true };
      } catch (error) {
        return { autoApprove: true } as ReviewsSettings;
      }
    },
  });

  // Update review status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      reviewId,
      status,
    }: {
      reviewId: string;
      status: 'APPROVED' | 'REJECTED';
    }) => {
      return await apiClient.patch(`/products/reviews/${reviewId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-reviews', activeStoreId] });
      setSelectedReview(null);
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: ReviewsSettings) => {
      return await apiClient.patch('/products/reviews/settings', settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-settings', activeStoreId] });
    },
  });

  const reviews = Array.isArray(reviewsData) ? reviewsData : [];

  const filteredReviews = reviews.filter(
    (review) =>
      (review.product?.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${review.customer?.firstName || ''} ${review.customer?.lastName || ''}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const pendingCount = reviews.filter((r) => r.status === 'PENDING').length;
  const approvedCount = reviews.filter((r) => r.status === 'APPROVED').length;
  const rejectedCount = reviews.filter((r) => r.status === 'REJECTED').length;

  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((acc, r) => acc + (r.rating || 0), 0) / reviews.length
        ).toFixed(1)
      : '0.0';

  const autoApproveEnabled =
    settingsData?.autoApprove !== undefined ? settingsData.autoApprove : true;

  const getStatusBadge = (status: Review['status']) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            Pending Moderation
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] dark:bg-[#006838]/20 text-[#006838] dark:text-emerald-400 border border-[#BDE5B3] dark:border-emerald-800">
            Published
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
            Rejected
          </span>
        );
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="inline-flex items-center gap-0.5 text-amber-500">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            weight={star <= rating ? 'fill' : 'regular'}
            className="w-3.5 h-3.5"
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
            <ChatCenteredText weight="fill" className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              Customer Reviews & Ratings
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Moderate buyer feedback, respond to questions, and automate store publishing rules
            </p>
          </div>
        </div>

        {/* Auto-Approve Strip */}
        <div className="flex items-center gap-3 px-3.5 py-2 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
          <div>
            <span className="text-xs font-bold text-[#202223] dark:text-white block">
              Auto-Publish Verified Reviews
            </span>
            <span className="text-[10px] text-[#5C5F62] dark:text-slate-400">
              Instantly approve 4★ and 5★ reviews from verified customers
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              updateSettingsMutation.mutate({ autoApprove: !autoApproveEnabled })
            }
            disabled={updateSettingsMutation.isPending}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
              autoApproveEnabled ? 'bg-[#006838]' : 'bg-[#D2D5D9] dark:bg-slate-700'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                autoApproveEnabled ? 'translate-x-4.5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* KPI Tiles Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button
          type="button"
          onClick={() => setStatusFilter('ALL')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'ALL'
              ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Total Reviews
          </span>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-0.5">
            {reviews.length}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('PENDING')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'PENDING'
              ? 'border-amber-500 ring-1 ring-amber-500 bg-amber-50/40'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
            Pending Review
          </span>
          <p className="text-xl font-bold text-amber-600 font-mono mt-0.5">
            {pendingCount}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('APPROVED')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'APPROVED'
              ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#006838]">
            Published
          </span>
          <p className="text-xl font-bold text-[#006838] font-mono mt-0.5">
            {approvedCount}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('REJECTED')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'REJECTED'
              ? 'border-red-500 ring-1 ring-red-500 bg-red-50/40'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-red-700">
            Rejected
          </span>
          <p className="text-xl font-bold text-red-600 font-mono mt-0.5">
            {rejectedCount}
          </p>
        </button>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 text-left">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Store Average Rating
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xl font-bold text-[#202223] dark:text-white font-mono">
              {averageRating}
            </span>
            <Star weight="fill" className="w-5 h-5 text-amber-500" />
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
          />
          <input
            type="text"
            placeholder="Search by customer name or product title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-[#5C5F62] inline-flex items-center gap-1">
            <Funnel weight="bold" className="w-3.5 h-3.5" />
            Showing <b>{filteredReviews.length}</b> review entries
          </span>
        </div>
      </div>

      {/* Moderation Directory Table */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-xs text-[#5C5F62]">
            <CircleNotch weight="bold" className="w-6 h-6 animate-spin mx-auto mb-2 text-[#006838]" />
            Loading customer reviews...
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="p-14 text-center">
            <ChatCenteredText weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">
              No Reviews Match Filter
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              Customer ratings and reviews will appear here as orders are delivered.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  <th className="py-2.5 px-4">Product</th>
                  <th className="py-2.5 px-4">Customer</th>
                  <th className="py-2.5 px-4">Rating & Review</th>
                  <th className="py-2.5 px-4">Submitted On</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Moderation Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {filteredReviews.map((review) => (
                  <tr
                    key={review.id}
                    className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="font-semibold text-[#202223] dark:text-white line-clamp-1 max-w-44">
                        {review.product?.title || 'Product'}
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-[#202223] dark:text-white">
                          {review.customer?.firstName} {review.customer?.lastName}
                        </span>
                        {review.verified && (
                          <span
                            title="Verified Purchase"
                            className="text-[#006FBB] inline-flex items-center"
                          >
                            <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-3 px-4 max-w-sm">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                          {review.title && (
                            <span className="font-bold text-[#202223] dark:text-white">
                              {review.title}
                            </span>
                          )}
                        </div>
                        <p className="text-[#5C5F62] dark:text-slate-400 line-clamp-2">
                          {review.comment || 'No written feedback provided.'}
                        </p>
                      </div>
                    </td>

                    <td className="py-3 px-4 text-[#5C5F62] whitespace-nowrap">
                      {review.createdAt
                        ? format(new Date(review.createdAt), 'MMM dd, yyyy')
                        : '—'}
                    </td>

                    <td className="py-3 px-4">{getStatusBadge(review.status)}</td>

                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedReview(review)}
                          className="h-7 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] font-semibold inline-flex items-center gap-1 transition-colors"
                        >
                          <Eye weight="bold" className="w-3.5 h-3.5" />
                          Details
                        </button>

                        {review.status !== 'APPROVED' && (
                          <button
                            type="button"
                            onClick={() =>
                              updateStatusMutation.mutate({
                                reviewId: review.id,
                                status: 'APPROVED',
                              })
                            }
                            disabled={updateStatusMutation.isPending}
                            className="h-7 px-2.5 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white font-semibold inline-flex items-center gap-1 transition-colors"
                          >
                            <ThumbsUp weight="bold" className="w-3.5 h-3.5" />
                            Approve
                          </button>
                        )}

                        {review.status !== 'REJECTED' && (
                          <button
                            type="button"
                            onClick={() =>
                              updateStatusMutation.mutate({
                                reviewId: review.id,
                                status: 'REJECTED',
                              })
                            }
                            disabled={updateStatusMutation.isPending}
                            className="h-7 px-2.5 rounded-lg border border-red-200 dark:border-red-900 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 font-semibold inline-flex items-center gap-1 transition-colors"
                          >
                            <ThumbsDown weight="bold" className="w-3.5 h-3.5" />
                            Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review Details Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl max-w-lg w-full p-5 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-[#202223] dark:text-white">
                  Review Details
                </span>
                {getStatusBadge(selectedReview.status)}
              </div>
              <button
                type="button"
                onClick={() => setSelectedReview(null)}
                className="p-1 rounded-lg text-[#5C5F62] hover:bg-[#F6F6F7]"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  Product
                </span>
                <p className="font-bold text-[#202223] dark:text-white">
                  {selectedReview.product?.title}
                </p>
              </div>

              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  Customer
                </span>
                <p className="font-bold text-[#202223] dark:text-white">
                  {selectedReview.customer?.firstName} {selectedReview.customer?.lastName}{' '}
                  {selectedReview.verified && (
                    <span className="text-[#006FBB] font-normal">(Verified Buyer)</span>
                  )}
                </p>
              </div>

              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  Rating Given
                </span>
                <div className="mt-0.5">{renderStars(selectedReview.rating)}</div>
              </div>

              {selectedReview.title && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                    Title
                  </span>
                  <p className="font-semibold text-[#202223] dark:text-white">
                    {selectedReview.title}
                  </p>
                </div>
              )}

              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  Customer Comment
                </span>
                <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 text-[#202223] dark:text-white mt-1">
                  {selectedReview.comment || 'No comment text provided.'}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-[#E1E4E8] dark:border-slate-800 pt-3">
              <button
                type="button"
                onClick={() => setSelectedReview(null)}
                className="h-8 px-3 rounded-lg border border-[#D2D5D9] text-xs font-semibold text-[#5C5F62] hover:bg-[#F6F6F7]"
              >
                Close
              </button>

              {selectedReview.status !== 'APPROVED' && (
                <button
                  type="button"
                  onClick={() =>
                    updateStatusMutation.mutate({
                      reviewId: selectedReview.id,
                      status: 'APPROVED',
                    })
                  }
                  disabled={updateStatusMutation.isPending}
                  className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
                >
                  <ThumbsUp weight="bold" className="w-3.5 h-3.5" />
                  Approve Review
                </button>
              )}

              {selectedReview.status !== 'REJECTED' && (
                <button
                  type="button"
                  onClick={() =>
                    updateStatusMutation.mutate({
                      reviewId: selectedReview.id,
                      status: 'REJECTED',
                    })
                  }
                  disabled={updateStatusMutation.isPending}
                  className="h-8 px-4 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
                >
                  <ThumbsDown weight="bold" className="w-3.5 h-3.5" />
                  Reject & Hide
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
